<script setup lang="ts">
import { ref, onMounted, nextTick, toRefs, inject, computed } from 'vue';
import ArrowDownIcon from '../assets/icons/ArrowDown.vue';
import { Lot } from '@/types';
const props = defineProps<{
  lotInformationData: Lot;
}>();
const { lotInformationData } = toRefs(props);
const isPublicNotesExpanded = ref(false);
const isPrivateNotesExpanded = ref(false);
const showPublicReadMore = ref(false);
const showPrivateReadMore = ref(false);
const publicNotesRef = ref<HTMLElement | null>(null);
const privateNotesRef = ref<HTMLElement | null>(null);

const mode = inject('mode', ref(''));
const isLandView = computed(() => mode.value === 'land');

const togglePublicExpand = () => {
  isPublicNotesExpanded.value = !isPublicNotesExpanded.value;
};
const togglePrivateExpand = () => {
  isPrivateNotesExpanded.value = !isPrivateNotesExpanded.value;
};

onMounted(async () => {
  await nextTick();
  showPrivateReadMore.value = getElementReadmoreState(privateNotesRef.value)
  showPublicReadMore.value = getElementReadmoreState(publicNotesRef.value)
});

const getElementReadmoreState = (el: any) => {
 if (el) {
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    const maxLines = 2;
    const maxHeight = lineHeight * maxLines;

    if (el.scrollHeight > maxHeight + 1) {
      return true;
    }
  }
  return false;
}
</script>

<template>
  <div v-if="lotInformationData" class="lot-detail">
    <h5>Job {{ lotInformationData.id }} Information</h5>
    <p class="lot-detail-dis" v-if="lotInformationData.id">
      <span class="lot-detail-label">Job ID</span> {{ lotInformationData.id }}
    </p>
    <p class="lot-detail-dis" v-if="lotInformationData.net_premium">
      <span class="lot-detail-label">Lot Differential</span>
      ${{ lotInformationData.net_premium.toLocaleString() }}
    </p>
    <p class="lot-detail-dis" v-if="lotInformationData.garage_hand">
      <span class="lot-detail-label">Garage Hand</span>
      {{ lotInformationData.garage_hand }}
    </p>
    <p class="lot-detail-dis" v-if="lotInformationData.drive_side">
      <span class="lot-detail-label">Driveway Placement</span>
      {{ lotInformationData.drive_side }}
    </p>
    <p class="lot-detail-dis" v-if="lotInformationData.rear_grade">
      <span class="lot-detail-label">Rear Grade</span>
      {{ lotInformationData.rear_grade }}
    </p>
    <p class="lot-detail-dis flex gap-1" v-if="lotInformationData.address">
      <span class="lot-detail-label">Address:</span>
      {{ lotInformationData.address && `${lotInformationData.address},` }} {{ lotInformationData.city  }} {{ lotInformationData.state }}, {{ lotInformationData.zip }}
    </p>
    <p
      class="lot-detail-dis lot-detail-notes"
      v-if="lotInformationData.public_note"
    >
      <span class="lot-detail-label">Public Notes</span
      ><span
        ref="publicNotesRef"
        :class="['lot-detail-value', { 'clamp-text': !isPublicNotesExpanded }]"
        >{{ lotInformationData.public_note }}</span
      >
      <span v-if="showPublicReadMore" class="toggle-read" @click="togglePublicExpand">
        {{ isPublicNotesExpanded ? 'Read Less' : 'Read More' }}
        &nbsp;<ArrowDownIcon :class="{ 'rotate-180': isPublicNotesExpanded }" />
      </span>
    </p>
    <p
      class="lot-detail-dis lot-detail-notes"
      v-if="lotInformationData.private_note && isLandView"
    >
      <span class="lot-detail-label">Private Notes</span
      ><span
        ref="privateNotesRef"
        :class="['lot-detail-value', { 'clamp-text': !isPrivateNotesExpanded }]"
        >{{ lotInformationData.private_note }}</span
      >
      <span v-if="showPrivateReadMore" class="toggle-read" @click="togglePrivateExpand">
        {{ isPrivateNotesExpanded ? 'Read Less' : 'Read More' }}
        &nbsp;<ArrowDownIcon :class="{ 'rotate-180': isPrivateNotesExpanded }" />
      </span>
    </p>
  </div>
</template>

<style lang="scss" scoped>
.lot-detail {
  color: white;
  margin-bottom: 24px;
  &-dis {
    font-size: 12px;
  }
  &-label {
    font-weight: 600;
  }
  &-value {
    display: block;
    margin-top: 8px;
  }
  &-notes {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
  }
  h5 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
  }
}

.clamp-text {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.toggle-read {
  color: #b6ce1e;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  align-self: flex-end;
  font-size: 12.5px;
}
</style>
